import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const GtmshPrivacy = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>GTM Super Hero Privacy Policy - Tagnetica</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
        <meta name="bingbot" content="noindex, nofollow" />
        <meta name="description" content="GTM Super Hero Privacy Policy" />
      </Helmet>
      
      <Header />
      
      <main className="pt-16">
        <div className="container mx-auto px-6 lg:px-8 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <h1 className="text-4xl font-bold text-text-primary mb-6">Privacy Policy</h1>
              <p className="text-lg text-text-secondary mb-8">
                <strong>Last updated: 28/06/2025</strong>
              </p>

              <div className="space-y-8 text-text-secondary">
                <section>
                  <h2 className="text-2xl font-semibold text-text-primary mb-4">1. Introduction</h2>
                  <p>
                    Tagnetica ("we," "our," "us," or "Company"), operating GTM Super Hero, is committed to protecting your privacy. 
                    This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our 
                    web-based toolkit for analyzing Google Tag Manager (GTM) container exports.
                  </p>
                  <p className="bg-accent/50 border border-primary/20 rounded-lg p-4 mt-4">
                    <strong>IMPORTANT:</strong> BY USING THE SERVICE, YOU CONSENT TO THE COLLECTION, USE, AND DISCLOSURE OF YOUR 
                    INFORMATION AS DESCRIBED IN THIS PRIVACY POLICY. IF YOU DO NOT AGREE WITH THIS POLICY, PLEASE DO NOT USE OUR SERVICE.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-text-primary mb-4">2. Information We Collect</h2>
                  
                  <h3 className="text-xl font-semibold text-text-primary mb-3">2.1 Information You Provide</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Account Information:</strong> When you create an account, we collect your email address, name, and profile picture (via Google OAuth)</li>
                    <li><strong>Usage Data:</strong> We track your usage of the Service, including the number of GTM imports and regex generations</li>
                    <li><strong>Payment Information:</strong> For Pro tier subscriptions, payment information is processed by Stripe (we do not store payment details)</li>
                    <li><strong>Communications:</strong> Any information you provide when contacting us for support or feedback</li>
                  </ul>

                  <h3 className="text-xl font-semibold text-text-primary mb-3 mt-6">2.2 Information Collected Automatically</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Log Data:</strong> Server logs including IP addresses, browser type, access times, and pages viewed</li>
                    <li><strong>Usage Analytics:</strong> Anonymous usage statistics to improve our service</li>
                    <li><strong>Cookies:</strong> Essential cookies for authentication and session management</li>
                    <li><strong>Device Information:</strong> Browser type, operating system, and device identifiers</li>
                  </ul>

                  <h3 className="text-xl font-semibold text-text-primary mb-3 mt-6">2.3 Information We Do Not Collect</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>GTM Container Content:</strong> We do not access, store, or process the actual content of your GTM containers</li>
                    <li><strong>Personal Data in GTM:</strong> Any personal or sensitive data within your GTM containers remains in your browser</li>
                    <li><strong>Third-Party Data:</strong> We do not collect data from third-party services beyond what's necessary for authentication</li>
                    <li><strong>Sensitive Personal Information:</strong> We do not intentionally collect sensitive personal information such as health data, financial information, or government identifiers</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-text-primary mb-4">3. How We Use Your Information</h2>
                  
                  <h3 className="text-xl font-semibold text-text-primary mb-3">3.1 Service Provision</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>To provide and maintain the GTM Super Hero service</li>
                    <li>To authenticate your account and manage your subscription</li>
                    <li>To track usage limits for free tier users</li>
                    <li>To process payments for Pro tier subscriptions</li>
                    <li>To provide customer support and respond to inquiries</li>
                  </ul>

                  <h3 className="text-xl font-semibold text-text-primary mb-3 mt-6">3.2 Service Improvement</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>To analyze usage patterns and improve our service</li>
                    <li>To develop new features and functionality</li>
                    <li>To optimize performance and user experience</li>
                    <li>To conduct research and analytics</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-text-primary mb-4">4. How We Share Your Information</h2>
                  
                  <h3 className="text-xl font-semibold text-text-primary mb-3">4.1 Service Providers</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Google:</strong> For OAuth authentication (subject to Google's Privacy Policy)</li>
                    <li><strong>Stripe:</strong> For payment processing (subject to Stripe's Privacy Policy)</li>
                    <li><strong>Database Hosting:</strong> For storing account and usage data</li>
                    <li><strong>Analytics Services:</strong> For anonymous usage analytics</li>
                    <li><strong>Email Services:</strong> For sending communications</li>
                  </ul>
                  <p className="mt-4">
                    <strong>All service providers are contractually obligated to protect your information and use it only for specified purposes.</strong>
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-text-primary mb-4">5. Data Security</h2>
                  
                  <h3 className="text-xl font-semibold text-text-primary mb-3">5.1 Security Measures</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>We use industry-standard encryption for data transmission (TLS/SSL)</li>
                    <li>We implement access controls and authentication measures</li>
                    <li>We regularly update our security practices and conduct security audits</li>
                    <li>We monitor for potential security threats and vulnerabilities</li>
                    <li>We use secure hosting providers with robust security measures</li>
                  </ul>

                  <h3 className="text-xl font-semibold text-text-primary mb-3 mt-6">5.2 Data Processing</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>GTM container data is processed entirely in your browser</li>
                    <li>We do not store GTM container content on our servers</li>
                    <li>Account and usage data is stored securely in our database</li>
                    <li>We use secure APIs and follow security best practices</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-text-primary mb-4">6. Your Rights and Choices</h2>
                  
                  <h3 className="text-xl font-semibold text-text-primary mb-3">6.1 Access and Correction</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>You can access and update your account information</li>
                    <li>You can view your usage statistics</li>
                    <li>You can correct inaccurate information</li>
                    <li>You can request a copy of your personal data</li>
                  </ul>

                  <h3 className="text-xl font-semibold text-text-primary mb-3 mt-6">6.2 GDPR Compliance</h3>
                  <p>If you are in the European Union, you have additional rights under GDPR:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Right to be informed about data processing</li>
                    <li>Right of access to your personal data</li>
                    <li>Right to rectification of inaccurate data</li>
                    <li>Right to erasure ("right to be forgotten")</li>
                    <li>Right to restrict processing</li>
                    <li>Right to data portability</li>
                    <li>Right to object to processing</li>
                    <li>Rights related to automated decision making</li>
                    <li>Right to withdraw consent</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-text-primary mb-4">7. Contact Us</h2>
                  <p>
                    If you have any questions about this Privacy Policy or our data practices, please contact us:
                  </p>
                  <div className="bg-accent/30 border border-primary/20 rounded-xl p-6 mt-4">
                    <ul className="list-none space-y-2">
                      <li><strong>Email:</strong> hello@tagnetica.co.uk</li>
                      <li><strong>Website:</strong> https://tagimplementation.tagnetica.co.uk</li>
                    </ul>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default GtmshPrivacy; 